import React, { useCallback, useEffect, useState } from "react";

import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";

import { AppBreadcrumbs } from "@shell/components/AppBreadcrumbs";
import { getContainerStyles } from "@shell/styles/themeUtils";
import type {
  CustomerIdentityActivationStatus,
  IdentityClient,
  IdentityClientType,
  IdentityProvider,
  IdentityUser,
} from "src/domains/platform-products/api/customerIdentityTypes";
import { useCustomerIdentityApi } from "src/domains/platform-products/api/useCustomerIdentityApi";
import {
  ORGANIZATIONS,
  PLATFORM_PRODUCTS,
} from "src/platform/constants/routes";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

// ── Tab panel helper ────────────────────────────────────────────────────────

const TabPanel: React.FC<{
  children: React.ReactNode;
  value: number;
  index: number;
}> = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      id={`identity-tabpanel-${index}`}
      aria-labelledby={`identity-tab-${index}`}
    >
      {children}
    </Box>
  );
};

// ── Product mark ────────────────────────────────────────────────────────────

const ProductMark: React.FC = () => (
  <Box
    sx={{
      width: 44,
      height: 44,
      borderRadius: 1.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.25rem",
      bgcolor: "rgba(124, 58, 237, 0.18)",
      border: "1px solid rgba(167, 139, 250, 0.35)",
      color: "secondary.light",
      flexShrink: 0,
    }}
  >
    {"\u25CE"}
  </Box>
);

// ── Client type badge ───────────────────────────────────────────────────────

const clientTypeColor: Record<
  IdentityClientType,
  "info" | "warning" | "success"
> = {
  public: "info",
  confidential: "warning",
  machine: "success",
};

// ── Main component ──────────────────────────────────────────────────────────

const CustomerIdentityManagementPage: React.FC = () => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias } = useParams<{ orgAlias: string }>();
  const identity = useCustomerIdentityApi();

  const [activeTab, setActiveTab] = useState(0);
  const [activationStatus, setActivationStatus] =
    useState<CustomerIdentityActivationStatus>("not_activated");
  const [activating, setActivating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [clients, setClients] = useState<IdentityClient[]>([]);
  const [users, setUsers] = useState<IdentityUser[]>([]);
  const [providers, setProviders] = useState<IdentityProvider[]>([]);

  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showAddProvider, setShowAddProvider] = useState(false);

  // ── Form state ────────────────────────────────────────────────────────
  const [clientForm, setClientForm] = useState({
    clientAlias: "",
    clientType: "public" as IdentityClientType,
    clientName: "",
    clientDescription: "",
    redirectUris: "",
    postLogoutRedirectUris: "",
    scopes: "openid profile email",
  });
  const [userForm, setUserForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    role: "user" as "admin" | "user",
  });
  const [providerForm, setProviderForm] = useState({
    alias: "",
    displayName: "",
    metadataUrl: "",
    clientId: "",
    clientSecret: "",
  });

  // ── Load activation status ────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    if (!orgAlias) return;
    setLoading(true);
    setLoadError(false);
    try {
      const result = await identity.getStatus.call({
        pathParams: { alias: orgAlias },
      });
      setActivationStatus(result?.status ?? "not_activated");
      if (result?.status === "active") {
        await Promise.all([loadClients(), loadUsers(), loadProviders()]);
      }
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [orgAlias]);

  const loadClients = useCallback(async () => {
    if (!orgAlias) return;
    try {
      const result = await identity.listClients.call({
        pathParams: { alias: orgAlias },
      });
      setClients(result ?? []);
    } catch {
      /* empty state */
    }
  }, [orgAlias, identity]);

  const loadUsers = useCallback(async () => {
    if (!orgAlias) return;
    try {
      const result = await identity.listUsers.call({
        pathParams: { alias: orgAlias },
      });
      setUsers(result?.users ?? []);
    } catch {
      /* empty state */
    }
  }, [orgAlias, identity]);

  const loadProviders = useCallback(async () => {
    if (!orgAlias) return;
    try {
      const result = await identity.listProviders.call({
        pathParams: { alias: orgAlias },
      });
      setProviders(result ?? []);
    } catch {
      /* empty state */
    }
  }, [orgAlias, identity]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleActivate = async () => {
    if (!orgAlias) return;
    setActivating(true);
    setError(null);
    try {
      await identity.activate.call({ pathParams: { alias: orgAlias } });
      setActivationStatus("active");
      await Promise.all([loadClients(), loadUsers(), loadProviders()]);
    } catch {
      setError(
        "Could not activate Customer Identity. Check your organization permissions.",
      );
    } finally {
      setActivating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!orgAlias) return;
    setError(null);
    try {
      await identity.deactivate.call({ pathParams: { alias: orgAlias } });
      setActivationStatus("not_activated");
      setClients([]);
      setUsers([]);
      setProviders([]);
    } catch {
      setError("Could not deactivate Customer Identity.");
    }
  };

  const handleCreateClient = async () => {
    if (!orgAlias) return;
    setError(null);
    try {
      await identity.createClient.call({
        pathParams: { alias: orgAlias },
        body: {
          clientAlias: clientForm.clientAlias,
          clientType: clientForm.clientType,
          clientName: clientForm.clientName,
          clientDescription: clientForm.clientDescription || undefined,
          redirectUris: clientForm.redirectUris.split("\n").filter(Boolean),
          postLogoutRedirectUris: clientForm.postLogoutRedirectUris
            .split("\n")
            .filter(Boolean),
          scopes: clientForm.scopes.split(/\s+/).filter(Boolean),
        },
      });
      setShowCreateClient(false);
      await loadClients();
    } catch {
      setError("Could not create the identity client.");
    }
  };

  const handleRotateClient = async (clientId: number) => {
    if (!orgAlias) return;
    try {
      await identity.rotateClient.call({
        pathParams: { alias: orgAlias, clientId: String(clientId) },
      });
      await loadClients();
    } catch {
      setError("Could not rotate credentials.");
    }
  };

  const handleRevokeClient = async (clientId: number) => {
    if (!orgAlias) return;
    try {
      await identity.revokeClient.call({
        pathParams: { alias: orgAlias, clientId: String(clientId) },
      });
      await loadClients();
    } catch {
      setError("Could not revoke the client.");
    }
  };

  const handleDownloadCredentials = async (client: IdentityClient) => {
    if (!orgAlias) return;
    try {
      const creds = await identity.getClientCredentials.call({
        pathParams: { alias: orgAlias, clientId: String(client.id) },
      });
      if (!creds) return;
      const envContent = [
        `# Customer Identity Client Credentials`,
        `# Organization: ${orgAlias}`,
        `# Client: ${client.clientAlias}`,
        `# Generated: ${new Date().toISOString()}`,
        ``,
        `CLIENT_ID=${creds.clientId}`,
        ...(creds.clientSecret ? [`CLIENT_SECRET=${creds.clientSecret}`] : []),
        `TOKEN_ENDPOINT=${creds.tokenEndpoint}`,
        `ISSUER=${creds.issuer ?? ""}`,
        ...(creds.jwksUri ? [`JWKS_URI=${creds.jwksUri}`] : []),
      ].join("\n");
      const blob = new Blob([envContent], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `identity-client-${client.clientAlias}-${orgAlias}.env`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError(
        "Could not retrieve credentials. Ensure the client is ready and you have admin permissions.",
      );
    }
  };

  const [editingUser, setEditingUser] = useState<number | null>(null);

  const handleEditUser = async (user: IdentityUser, _idx: number) => {
    if (!orgAlias || !user.email) return;
    try {
      await identity.updateUser.call({
        pathParams: { alias: orgAlias, userId: user.email },
        body: {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
        },
      });
      setEditingUser(null);
      await loadUsers();
    } catch {
      setError("Could not update the user.");
    }
  };

  const handleDeleteUser = async (user: IdentityUser) => {
    if (!orgAlias || !user.email) return;
    try {
      await identity.deleteUser.call({
        pathParams: { alias: orgAlias, userId: user.email },
      });
      await loadUsers();
    } catch {
      setError("Could not delete the user.");
    }
  };

  const handleCreateUser = async () => {
    if (!orgAlias) return;
    setError(null);
    try {
      await identity.createUser.call({
        pathParams: { alias: orgAlias },
        body: {
          email: userForm.email,
          firstName: userForm.firstName,
          lastName: userForm.lastName,
          role: userForm.role,
        },
      });
      setShowCreateUser(false);
      await loadUsers();
    } catch {
      setError("Could not create the user.");
    }
  };

  const handleAddProvider = async () => {
    if (!orgAlias) return;
    setError(null);
    try {
      await identity.addProvider.call({
        pathParams: { alias: orgAlias },
        body: {
          alias: providerForm.alias,
          displayName: providerForm.displayName,
          providerType: "oidc",
          metadataUrl: providerForm.metadataUrl,
          clientId: providerForm.clientId,
          clientSecret: providerForm.clientSecret,
        },
      });
      setShowAddProvider(false);
      await loadProviders();
    } catch {
      setError("Could not add the identity provider.");
    }
  };

  const handleRemoveProvider = async (providerAlias: string) => {
    if (!orgAlias) return;
    try {
      await identity.removeProvider.call({
        pathParams: { alias: orgAlias, providerAlias },
      });
      await loadProviders();
    } catch {
      setError("Could not remove the identity provider.");
    }
  };

  if (!orgAlias) return null;

  const tabs = [
    "Overview",
    "App clients",
    "Users",
    "Identity providers",
    "Domains",
    "Settings",
    "Audit",
  ];
  const isActive = activationStatus === "active";

  // ── Error / loading states ─────────────────────────────────────────────
  if (loadError) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <AppBreadcrumbs
          items={[
            { label: "Organizations", to: ORGANIZATIONS.LIST.path },
            { label: orgAlias, to: ORGANIZATIONS.DETAILS.getPath(orgAlias) },
            {
              label: PLATFORM_PRODUCTS.CATALOG.title,
              to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
            },
            { label: "Customer Identity" },
          ]}
        />
        <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
          <CardContent>
            <Stack
              spacing={2}
              alignItems="center"
              sx={{ textAlign: "center", py: 4 }}
            >
              <Typography variant="h5" fontWeight={700}>
                Customer Identity unavailable
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={480}>
                The workspace could not be loaded. Try again after the service
                recovers.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => void loadStatus()}
                sx={{ textTransform: "none" }}
              >
                Try again
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={getContainerStyles(theme)}>
        <AppBreadcrumbs
          items={[
            { label: "Organizations", to: ORGANIZATIONS.LIST.path },
            { label: orgAlias, to: ORGANIZATIONS.DETAILS.getPath(orgAlias) },
            {
              label: PLATFORM_PRODUCTS.CATALOG.title,
              to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
            },
            { label: "Customer Identity" },
          ]}
        />
        <Stack spacing={2} sx={{ mt: 3 }}>
          <Typography variant="h4" fontWeight={700}>
            Loading Customer Identity…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Product management data is being retrieved for {orgAlias}.
          </Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Container sx={getContainerStyles(theme)}>
      <AppBreadcrumbs
        items={[
          { label: "Organizations", to: ORGANIZATIONS.LIST.path },
          { label: orgAlias, to: ORGANIZATIONS.DETAILS.getPath(orgAlias) },
          {
            label: PLATFORM_PRODUCTS.CATALOG.title,
            to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
          },
          { label: "Customer Identity" },
        ]}
      />

      <Stack spacing={3}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ProductMark />
              <Typography variant="h4" component="h1" fontWeight={700}>
                Customer Identity
              </Typography>
              <Chip
                label={isActive ? "Active" : "Not activated"}
                color={isActive ? "success" : "default"}
                variant="outlined"
                size="small"
              />
            </Stack>
            <Typography variant="body1" color="text.secondary" maxWidth={720}>
              A managed identity service where customer organizations register
              OIDC clients for their own applications, manage user directories,
              and federate from external identity providers.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexShrink={0}>
            {isActive ? (
              <Button
                variant="outlined"
                color="error"
                onClick={() => void handleDeactivate()}
                sx={{ textTransform: "none" }}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => void handleActivate()}
                disabled={activating}
                sx={{ textTransform: "none" }}
              >
                {activating ? "Activating…" : "Activate Customer Identity"}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => appNavigation.goToPlatformProducts(orgAlias)}
              sx={{ textTransform: "none" }}
            >
              Back to products
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        {/* ── Not activated state ────────────────────────────────────────── */}
        {!isActive && (
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>
                  Activate Customer Identity
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Activating provisions an org-scoped Keycloak group for your
                  user directory. After activation, you can register application
                  clients, manage users, and configure federation.
                </Typography>
                <Alert severity="info">
                  <Typography variant="body2">
                    Activation creates an org-scoped Keycloak group (
                    <strong>org-{orgAlias}</strong>) and a default org-scoped
                    client scope set. Individual clients are registered
                    separately after activation.
                  </Typography>
                </Alert>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={() => void handleActivate()}
                    disabled={activating}
                    sx={{ textTransform: "none" }}
                  >
                    {activating ? "Activating…" : "Activate now"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* ── Active workspace with 7 tabs ───────────────────────────────── */}
        {isActive && (
          <Stack spacing={2}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              aria-label="Customer Identity management tabs"
            >
              {tabs.map((label) => (
                <Tab key={label} label={label} sx={{ textTransform: "none" }} />
              ))}
            </Tabs>

            {/* ── Overview tab ────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Registered clients
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {clients.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Managed users
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {users.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">
                        Federation connections
                      </Typography>
                      <Typography variant="h5" fontWeight={700}>
                        {providers.length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        Quick actions
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setActiveTab(1)}
                          sx={{ textTransform: "none" }}
                        >
                          Register client
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setActiveTab(2)}
                          sx={{ textTransform: "none" }}
                        >
                          Manage users
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setActiveTab(3)}
                          sx={{ textTransform: "none" }}
                        >
                          Configure federation
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>

            {/* ── App clients tab ─────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={1}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" fontWeight={700}>
                    Application clients
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateClient(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Register client
                  </Button>
                </Stack>
                {clients.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No clients registered yet. Click "Register client" to create
                    one.
                  </Typography>
                ) : (
                  <List>
                    {clients.map((client) => (
                      <ListItem key={client.id} divider>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {client.clientAlias}
                              </Typography>
                              <Chip
                                label={client.clientType}
                                color={clientTypeColor[client.clientType]}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={client.status}
                                size="small"
                                variant="outlined"
                                color={
                                  client.status === "ready"
                                    ? "success"
                                    : client.status === "failed"
                                      ? "error"
                                      : "default"
                                }
                              />
                            </Stack>
                          }
                          secondary={client.clientName}
                        />
                        <Stack direction="row" spacing={1}>
                          {client.status === "ready" && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                void handleDownloadCredentials(client)
                              }
                              sx={{ textTransform: "none" }}
                            >
                              Download
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => void handleRotateClient(client.id)}
                            sx={{ textTransform: "none" }}
                          >
                            Rotate
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => void handleRevokeClient(client.id)}
                            aria-label="Revoke client"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Stack>
            </TabPanel>

            {/* ── Users tab ───────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={2}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" fontWeight={700}>
                    User directory
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateUser(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Create user
                  </Button>
                </Stack>
                {users.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No users in the directory yet.
                  </Typography>
                ) : (
                  <List>
                    {users.map((user, idx) => (
                      <ListItem key={idx} divider>
                        <ListItemText
                          primary={
                            `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
                            user.email
                          }
                          secondary={user.email}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setEditingUser(editingUser === idx ? null : idx);
                              if (editingUser !== idx)
                                void handleEditUser(user, idx);
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            {editingUser === idx ? "Editing…" : "Edit"}
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => void handleDeleteUser(user)}
                            aria-label="Delete user"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Stack>
            </TabPanel>

            {/* ── Identity providers tab ──────────────────────────────────── */}
            <TabPanel value={activeTab} index={3}>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6" fontWeight={700}>
                    Identity providers
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddProvider(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Add provider
                  </Button>
                </Stack>
                {providers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No federation providers configured.
                  </Typography>
                ) : (
                  <List>
                    {providers.map((provider) => (
                      <ListItem key={provider.id} divider>
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {provider.displayName}
                              </Typography>
                              <Chip
                                label={provider.providerType.toUpperCase()}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={provider.status}
                                size="small"
                                variant="outlined"
                                color={
                                  provider.status === "ready"
                                    ? "success"
                                    : "default"
                                }
                              />
                            </Stack>
                          }
                          secondary={provider.alias}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            void handleRemoveProvider(provider.alias)
                          }
                          aria-label="Remove provider"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Stack>
            </TabPanel>

            {/* ── Domains tab (planned) ───────────────────────────────────── */}
            <TabPanel value={activeTab} index={4}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack
                    spacing={2}
                    alignItems="center"
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Custom domains — coming soon
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      maxWidth={480}
                    >
                      Custom domain configuration for the customer identity
                      service is planned for a future release.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </TabPanel>

            {/* ── Settings tab ────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={5}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    Product settings
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Token protocol
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        OIDC (current)
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Environment
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        Derived from platform
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Custom scopes
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        No custom scopes configured
                      </Typography>
                    </Box>
                  </Stack>
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Settings changes require organization administrator
                      permission.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </TabPanel>

            {/* ── Audit tab ───────────────────────────────────────────────── */}
            <TabPanel value={activeTab} index={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      Audit activity
                    </Typography>
                    <Chip
                      label="Organization scope"
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  {clients.length === 0 &&
                  users.length === 0 &&
                  providers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No audit events recorded yet.
                    </Typography>
                  ) : (
                    <List dense>
                      {clients.map((c) => (
                        <ListItem key={`audit-c-${c.id}`} divider>
                          <ListItemText
                            primary={`Client ${c.clientAlias} — ${c.status}`}
                            secondary={c.readyAt ?? "—"}
                          />
                        </ListItem>
                      ))}
                      {providers.map((p) => (
                        <ListItem key={`audit-p-${p.id}`} divider>
                          <ListItemText
                            primary={`Provider ${p.displayName} — ${p.status}`}
                            secondary={p.createdAt ?? "—"}
                          />
                        </ListItem>
                      ))}
                      {users.map((u, idx) => (
                        <ListItem key={`audit-u-${idx}`} divider>
                          <ListItemText
                            primary={`User ${u.email} — ${u.status ?? "created"}`}
                            secondary={u.firstName ?? ""}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          </Stack>
        )}
      </Stack>

      {/* ── Create client dialog ─────────────────────────────────────────── */}
      <Dialog
        open={showCreateClient}
        onClose={() => setShowCreateClient(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Register application client</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Client alias"
              value={clientForm.clientAlias}
              onChange={(e) =>
                setClientForm({ ...clientForm, clientAlias: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Client name"
              value={clientForm.clientName}
              onChange={(e) =>
                setClientForm({ ...clientForm, clientName: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Description"
              value={clientForm.clientDescription}
              onChange={(e) =>
                setClientForm({
                  ...clientForm,
                  clientDescription: e.target.value,
                })
              }
              size="small"
              fullWidth
            />
            <TextField
              select
              label="Client type"
              value={clientForm.clientType}
              onChange={(e) =>
                setClientForm({
                  ...clientForm,
                  clientType: e.target.value as IdentityClientType,
                })
              }
              size="small"
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="public">Public (SPA/mobile with PKCE)</option>
              <option value="confidential">Confidential (server-side)</option>
              <option value="machine">Machine (M2M client_credentials)</option>
            </TextField>
            <TextField
              label="Redirect URIs (one per line)"
              value={clientForm.redirectUris}
              onChange={(e) =>
                setClientForm({ ...clientForm, redirectUris: e.target.value })
              }
              size="small"
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Post-logout redirect URIs (optional, one per line)"
              value={clientForm.postLogoutRedirectUris}
              onChange={(e) =>
                setClientForm({
                  ...clientForm,
                  postLogoutRedirectUris: e.target.value,
                })
              }
              size="small"
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Scopes (space-separated)"
              value={clientForm.scopes}
              onChange={(e) =>
                setClientForm({ ...clientForm, scopes: e.target.value })
              }
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowCreateClient(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateClient()}
            sx={{ textTransform: "none" }}
          >
            Register
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create user dialog ───────────────────────────────────────────── */}
      <Dialog
        open={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create managed user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Email"
              value={userForm.email}
              onChange={(e) =>
                setUserForm({ ...userForm, email: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="First name"
              value={userForm.firstName}
              onChange={(e) =>
                setUserForm({ ...userForm, firstName: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Last name"
              value={userForm.lastName}
              onChange={(e) =>
                setUserForm({ ...userForm, lastName: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              select
              label="Role"
              value={userForm.role}
              onChange={(e) =>
                setUserForm({
                  ...userForm,
                  role: e.target.value as "admin" | "user",
                })
              }
              size="small"
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowCreateUser(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateUser()}
            sx={{ textTransform: "none" }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add provider dialog ──────────────────────────────────────────── */}
      <Dialog
        open={showAddProvider}
        onClose={() => setShowAddProvider(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add OIDC identity provider</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Alias"
              value={providerForm.alias}
              onChange={(e) =>
                setProviderForm({ ...providerForm, alias: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Display name"
              value={providerForm.displayName}
              onChange={(e) =>
                setProviderForm({
                  ...providerForm,
                  displayName: e.target.value,
                })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Metadata URL (HTTPS)"
              value={providerForm.metadataUrl}
              onChange={(e) =>
                setProviderForm({
                  ...providerForm,
                  metadataUrl: e.target.value,
                })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Client ID"
              value={providerForm.clientId}
              onChange={(e) =>
                setProviderForm({ ...providerForm, clientId: e.target.value })
              }
              size="small"
              fullWidth
            />
            <TextField
              label="Client secret"
              type="password"
              value={providerForm.clientSecret}
              onChange={(e) =>
                setProviderForm({
                  ...providerForm,
                  clientSecret: e.target.value,
                })
              }
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowAddProvider(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleAddProvider()}
            sx={{ textTransform: "none" }}
          >
            Add provider
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomerIdentityManagementPage;
